import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  lazy,
  ReactNode,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { Models } from "@/api/types";
import { useTranslation } from "@/core/i18n";
import { storage } from "@/modules/storage";
import { useUser, useUserPreferences } from "modules/user/useUser/useUser";

import { normalizePersona } from "../adonis.helpers";
import { AdonisIdentity } from "../adonis.types";
import { AdvisorLauncher } from "../AdvisorLauncher/AdvisorLauncher";

const LazyAdonisChat = lazy(() => import("../AdonisChat/AdonisChat"));

const sessionKey = "adonis_chat_session_id";
const sessionDurationInMs = 60 * 60 * 1000; // 1 hour

export interface AdonisSession {
  id: string;
  expiresAt: number;
}

const createAdonisSession = (): AdonisSession => {
  const id = uuidv4();
  const expiresAt = Date.now() + sessionDurationInMs;
  const session = { id, expiresAt };
  storage.setItem(sessionKey, JSON.stringify(session));
  return session;
};

const loadSession = (): AdonisSession => {
  try {
    const stored = storage.getItem(sessionKey);
    if (stored) {
      const session = JSON.parse(stored) as AdonisSession;
      if (Date.now() < session.expiresAt) {
        return session;
      }
    }
  } catch (err) {
    console.warn("Invalid session, recreating...");
  }

  return createAdonisSession();
};

interface AdonisChatContextProps {
  isEnabled: boolean;
  isPersonalisationEnabled: boolean;
  openChat: () => void;
  closeChat: () => void;
  isOpen: boolean;
  hasMessages: boolean;
  setHasMessages: (hasMessages: boolean) => void;
  session: AdonisSession;
  resetSession: () => void;
  showCTABanner: boolean;
  setShowCTABanner: (show: boolean) => void;
  persona: AdonisIdentity;
  pendingMessage: string | null;
  openChatWithMessage: (message: string) => void;
  clearPendingMessage: () => void;
  isLauncherDismissed: boolean;
  dismissLauncher: () => void;
  platformLocation?: {
    bookAppointmentUrl?: string | null;
    phoneNumber?: string | null;
    email?: string | null;
  };
}

export const AdonisChatContext = createContext<AdonisChatContextProps>(
  {} as AdonisChatContextProps,
);

export const useAdonisChat = () => {
  const ctx = useContext(AdonisChatContext);
  if (!ctx) {
    throw new Error(`AdonisChatContext.Provider not found.`);
  }
  return ctx;
};

export const AdonisChatProvider = ({
  children,
  platformSettings,
}: {
  children: ReactNode;
  platformSettings?: Models<"PlatformSettingModel1"> | null;
}) => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpenedChat, setHasOpenedChat] = useState(false);
  const [hasMessages, setHasMessages] = useState(false);
  const [session, setSession] = useState<AdonisSession>(loadSession());
  const [showCTABanner, setShowCTABanner] = useState(true);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [isLauncherDismissed, setIsLauncherDismissed] = useState(false);

  const { user } = useUser();
  const { preferences } = useUserPreferences();
  const { t, i18n } = useTranslation();

  // Compute persona locally from user profile (gender), falling back to default.
  // Pronoun/objectPronoun are sourced from the active locale's dictionary (rather
  // than hardcoded English literals) so `{pronoun}`/`{objectPronoun}` placeholder
  // substitution (see replacePersonaPlaceholders) renders correctly in every
  // supported language. `i18n.language` is a dependency so switching locale
  // recomputes these values.
  const persona = useMemo<AdonisIdentity>(() => {
    const preferencesPersona = preferences?.aiPersona;
    if (preferencesPersona) {
      return normalizePersona({
        name: preferencesPersona,
        pronoun:
          preferencesPersona === "Aria"
            ? t("adonisIdentity.pronounFemale")
            : t("adonisIdentity.pronounMale"),
        objectPronoun:
          preferencesPersona === "Aria"
            ? t("adonisIdentity.objectPronounFemale")
            : t("adonisIdentity.objectPronounMale"),
      });
    }

    const gender = user?.gender?.trim().toLowerCase();
    if (gender === "male") {
      return normalizePersona({
        name: "Adonis",
        pronoun: t("adonisIdentity.pronounMale"),
        objectPronoun: t("adonisIdentity.objectPronounMale"),
      });
    }
    return normalizePersona({
      name: "Aria",
      pronoun: t("adonisIdentity.pronounFemale"),
      objectPronoun: t("adonisIdentity.objectPronounFemale"),
    });
    // `i18n.language` is intentionally listed even though it isn't referenced by
    // name in this callback: it's what actually changes when the user switches
    // locale, and `t`'s function identity alone isn't guaranteed to change, so
    // omitting it would leave `persona` stale after a language switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences?.aiPersona, user?.gender, i18n.language, t]);

  // Extract platform location data for button identification
  const platformLocation = useMemo(
    () =>
      user?.platformLocation
        ? {
            bookAppointmentUrl: user.platformLocation.bookAppointmentUrl,
            phoneNumber: user.platformLocation.phoneNumber,
            email: user.platformLocation.email,
          }
        : undefined,
    [user?.platformLocation],
  );

  const clearPendingMessage = () => {
    setPendingMessage(null);
  };

  const dismissLauncher = () => {
    setIsLauncherDismissed(true);
  };

  const resetSession = () => {
    const newSession = createAdonisSession();
    setSession(newSession);
    setPendingMessage(null);
  };

  useEffect(() => {
    // Do NOT rotate the session while the chat modal is open. A rotation between /adonis/warm
    // (which warms session A) and /adonis/recommendation (which would then submit under session
    // B) leaves the new session with no warmup row and surfaces to the user as a bogus
    // assistant reply. Rotation can only happen safely when the modal is closed; the next open
    // will trigger a fresh warmup for whichever session id is current at that point.
    if (isOpen) {
      return;
    }

    const now = Date.now();
    const timeUntilExpiry = session.expiresAt - now;

    if (timeUntilExpiry <= 0) {
      const newSession = createAdonisSession();
      setSession(newSession);
      return;
    }

    const timeout = setTimeout(() => {
      const newSession = createAdonisSession();
      setSession(newSession);
    }, timeUntilExpiry);

    return () => clearTimeout(timeout);
  }, [session.expiresAt, isOpen]);

  const openChat = () => {
    setHasOpenedChat(true);
    setIsOpen(true);
    queryClient.invalidateQueries({
      queryKey: ["recommendations-history", session.id],
    });
  };

  const openChatWithMessage = (message: string) => {
    setPendingMessage(message);
    openChat();
  };

  const closeChat = () => {
    // @ts-expect-error - pdpNavigate is not defined in the window object
    window.pdpNavigate = undefined;
    setIsOpen(false);
    setPendingMessage(null);
  };

  useEffect(() => {
    if (isOpen) {
      setIsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <AdonisChatContext.Provider
      value={{
        isEnabled: (user && !user.isGuest && platformSettings?.aiAdvisorsEnabled) ?? false,
        isPersonalisationEnabled:
          (user && !user.isGuest && platformSettings?.patientPersonalisationEnabled) ?? false,
        openChat,
        closeChat,
        isOpen,
        hasMessages,
        setHasMessages,
        session,
        resetSession,
        showCTABanner,
        setShowCTABanner,
        persona,
        pendingMessage,
        openChatWithMessage,
        clearPendingMessage,
        isLauncherDismissed,
        dismissLauncher,
        platformLocation,
      }}
    >
      {children}
      {user && !user.isGuest && hasOpenedChat && (
        <Suspense fallback={null}>
          <LazyAdonisChat
            isOpen={isOpen}
            onClose={closeChat}
            session={session}
            resetSession={resetSession}
          />
        </Suspense>
      )}
      {user && !user.isGuest && <AdvisorLauncher />}
    </AdonisChatContext.Provider>
  );
};
